import React, { type ComponentType } from 'react';
import '@atlaskit/css-reset';
import { type EntryPointProps, usePreloadedQuery, graphql } from 'react-relay';
import { Box, xcss } from '@atlaskit/primitives';
import AppBase from '@atlassian/jira-app-base/src/index.tsx';
import { ProjectContextProvider } from '@atlassian/jira-business-entity-project-provider/src/ui/project-context/ProjectContextProvider.tsx';
import { ListFeaturesProvider } from '@atlassian/jira-business-list/src/controllers/features-context/index.tsx';
import ListView from '@atlassian/jira-business-list/src/index.tsx';
import { SeenIt } from '@atlassian/jira-business-onboarding-common/src/ui/seen-it/index.tsx';
import { LIST_VIEW_PREFERENCES } from '@atlassian/jira-business-preferences/src/constants.tsx';
import { ViewPreferencesProvider } from '@atlassian/jira-business-preferences/src/controllers/view-preferences-context/index.tsx';
import { useBoard } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/board/index.tsx';
import { getWillShowNav3 } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-core/src/common/utils/get-will-show-nav3.tsx';
import { getWillShowNav4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-core/src/common/utils/get-will-show-nav4/index.tsx';
import { useFeatures } from '@atlassian/jira-navigation-apps-sidebar-next-gen-use-features/src/controllers/features/index.tsx';
import type { formatRulesQuery as FormatRulesQuery } from '@atlassian/jira-relay/src/__generated__/formatRulesQuery.graphql';
import type { src_jiraSoftwareListQuery } from '@atlassian/jira-relay/src/__generated__/src_jiraSoftwareListQuery.graphql';
import { SubProductUpdater } from '@atlassian/jira-spa-apps-common/src/analytics-sub-product/sub-product-updater/index.tsx';
import { AuthenticationCheck } from '@atlassian/jira-spa-apps-common/src/interceptors/authentication/index.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { PageHeader } from './ui/page-header/index.tsx';

const LIST_NAV_ITEM_SEEN_IT_FLAG = 'jsw.list.nav-item-flag';

type JiraSoftwareListAppType = EntryPointProps<
	{ listQuery: src_jiraSoftwareListQuery; formattingRulesQuery: FormatRulesQuery },
	{},
	{},
	{}
>;

const JiraSoftwareListApp: ComponentType<JiraSoftwareListAppType> = ({
	queries: { listQuery, formattingRulesQuery },
}) => {
	const { data: features } = useFeatures();
	const { data: board } = useBoard();
	const isScrumProject = board?.type === 'scrum' || features?.hasSprint;

	const data = usePreloadedQuery<src_jiraSoftwareListQuery>(
		graphql`
			query src_jiraSoftwareListQuery($cloudId: ID!, $projectKey: String!) @preloadable {
				jira {
					jiraProjectByKey(cloudId: $cloudId, key: $projectKey) {
						projectStyle @required(action: THROW)
						createIssues: action(type: CREATE_ISSUES) @required(action: THROW) {
							canPerform @required(action: THROW)
						}
						...ProjectContextProvider
					}
					isSubtasksEnabled(cloudId: $cloudId) @optIn(to: "JiraIsSubtasksEnabled")
				}
				...src_businessList_List_queryFragment
			}
		`,
		listQuery,
	);

	const isSimplifiedProject = data.jira?.jiraProjectByKey?.projectStyle === 'TEAM_MANAGED_PROJECT';
	const isSubtasksEnabled = data.jira?.isSubtasksEnabled ?? false;
	const canCreateIssues = data.jira?.jiraProjectByKey?.createIssues?.canPerform ?? false;
	const canCreateSubtask = canCreateIssues && (isSimplifiedProject || isSubtasksEnabled);

	return (
		<UFOSegment name="software-list-app">
			<AuthenticationCheck>
				<AppBase id="software-list">
					<ProjectContextProvider projectFragment={data.jira?.jiraProjectByKey}>
						<ListFeaturesProvider
							featureView="software-list"
							hasCreateSubtask={canCreateSubtask}
							canCreateIssues={canCreateIssues}
							isSoftwareFieldsEnabled
							isBusinessFieldsEnabled={false}
							isScrumProject={isScrumProject}
							isShareButtonEnabled={getWillShowNav3()}
						>
							<ViewPreferencesProvider view="list" preferences={LIST_VIEW_PREFERENCES}>
								<Box
									xcss={[
										!isVisualRefreshEnabled() && pageContainerStyles,
										fullHeightPageContainerStyles,
									]}
								>
									<Box xcss={listContainerStyles}>
										{getWillShowNav4() ? (
											<ListView queryFragment={data} formattingRulesQuery={formattingRulesQuery} />
										) : (
											<ListView
												queryFragment={data}
												header={<PageHeader />}
												formattingRulesQuery={formattingRulesQuery}
											/>
										)}
									</Box>
								</Box>
							</ViewPreferencesProvider>
						</ListFeaturesProvider>
					</ProjectContextProvider>
					<SubProductUpdater subProduct="software" />
					<SeenIt flag={LIST_NAV_ITEM_SEEN_IT_FLAG} />
				</AppBase>
			</AuthenticationCheck>
		</UFOSegment>
	);
};

const pageContainerStyles = xcss({
	paddingLeft: 'space.200',
});

const fullHeightPageContainerStyles = xcss({
	height: '100%',
});

const listContainerStyles = xcss({
	height: '100%',
});

export default JiraSoftwareListApp;
